<template>
  <div class="register-nurse-page">
    <page-cover :cover="cover" v-if="cover" />
    <div class="auth-page container py-5">
    <div class="row justify-content-center">
      <div class="col-md-7 col-lg-6">
        <div class="card auth-card">
          <h2 v-tx="'NurseRegistrationTitle'">{{ tx('title') }}</h2>
          <p class="muted" v-tx="'NurseRegistrationSubtitle'">{{ tx('subtitle') }}</p>

          <form @submit.prevent="onSubmit">
            <div class="grid">
              <label class="field">
                <span v-tx="'NurseRegistrationName'">{{ tx('name') }} *</span>
                <input type="text" v-model="form.name" required maxlength="100" />
              </label>
              <label class="field">
                <span v-tx="'NurseRegistrationSurname'">{{ tx('surname') }} *</span>
                <input type="text" v-model="form.surname" required maxlength="100" />
              </label>
            </div>
            <label class="field">
              <span v-tx="'NurseRegistrationEmail'">{{ tx('email') }} *</span>
              <input type="email" v-model="form.email" required />
            </label>
            <label class="field">
              <span v-tx="'NurseRegistrationPassword'">{{ tx('password') }} *</span>
              <input type="password" v-model="form.password" required minlength="6" />
            </label>
            <div class="grid">
              <label class="field">
                <span v-tx="'NurseRegistrationGender'">{{ tx('gender') }} *</span>
                <select v-model="form.gender" required>
                  <option value="" disabled>{{ tx('genderSelect') }}</option>
                  <option value="male">{{ tx('genderMale') }}</option>
                  <option value="female">{{ tx('genderFemale') }}</option>
                </select>
              </label>
              <label class="field">
                <span v-tx="'NurseRegistrationBirthDate'">{{ tx('birthDate') }}</span>
                <input type="date" v-model="form.birthDate" />
              </label>
            </div>
            <label class="field">
              <span v-tx="'NurseRegistrationCitizenship'">{{ tx('citizenship') }}</span>
              <input type="text" v-model="form.citizenship" maxlength="100" />
            </label>
            <label class="field">
              <span v-tx="'NurseRegistrationPhone'">{{ tx('phone') }}</span>
              <input type="tel" v-model="form.phone" maxlength="50" />
            </label>
            <label class="field">
              <span v-tx="'NurseRegistrationAddress'">{{ tx('address') }}</span>
              <textarea v-model="form.address" maxlength="500" rows="2"></textarea>
            </label>

            <label class="consent">
              <input type="checkbox" v-model="consentAccepted" required />
              <span v-tx="'NurseRegistrationConsent'">{{ consentText }}</span>
            </label>

            <button type="submit" class="primary" :disabled="busy || !consentAccepted || !form.gender" v-tx="'NurseRegistrationSubmit'">
              {{ busy ? tx('submitting') : tx('submit') }}
            </button>
          </form>

          <div class="alt">
            <router-link :to="{ name: 'login' }" v-tx="'NurseRegistrationHaveAccount'">{{ tx('haveAccount') }}</router-link>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import PageCover from "@/components/shared/PageCover.vue";

const CONSENT_FALLBACK = {
  az: "Mən razılıq verirəm ki, mənim profilim, diplomum, dil sertifikatı, CV-si və iş təcrübəsinə dair məlumatlar uyğun işəgötürənlərə təqdim edilsin. Şəxsi əlaqə məlumatları və digər həssas məlumatlar yalnız ayrıca razılıq əsasında paylaşılır.",
  en: "I consent that my profile, diploma, language certificate, CV and work experience information may be presented to suitable employers. Personal contact details and other sensitive data are shared only on the basis of separate consent.",
  de: "Ich willige ein, dass mein Profil, Diplom, Sprachzertifikat, Lebenslauf und Informationen zur Berufserfahrung geeigneten Arbeitgebern vorgelegt werden dürfen. Persönliche Kontaktdaten und andere sensible Daten werden nur auf Grundlage einer gesonderten Einwilligung weitergegeben.",
  ru: "Я даю согласие на то, чтобы мой профиль, диплом, языковой сертификат, резюме и сведения об опыте работы предоставлялись подходящим работодателям. Личные контактные данные и другие конфиденциальные сведения передаются только на основании отдельного согласия.",
  tr: "Profilimin, diplomamın, dil sertifikamın, özgeçmişimin ve iş deneyimi bilgilerimin uygun işverenlere sunulmasına onay veriyorum. Kişisel iletişim bilgileri ve diğer hassas veriler yalnızca ayrı bir onay temelinde paylaşılır.",
};

const REG_FALLBACK = {
  az: {
    title: "Tibb bacısı qeydiyyatı",
    subtitle: "Qeydiyyatdan sonra profil səhifənizdə CV-ni doldura bilərsiniz. Profiliniz admin tərəfindən təsdiqləndikdən sonra klinikalar onu görəcək.",
    name: "Ad", surname: "Soyad", email: "Email", password: "Şifrə",
    gender: "Cins", genderSelect: "Seçin…", genderMale: "Kişi", genderFemale: "Qadın",
    birthDate: "Doğum tarixi", citizenship: "Vətəndaşlıq", phone: "Telefon", address: "Ünvan",
    submit: "Qeydiyyatdan keç", submitting: "Göndərilir…", haveAccount: "Hesabınız var? Daxil olun",
    regFailed: "Qeydiyyat alınmadı", regSuccess: "Qeydiyyat uğurlu! İndi CV-nizi doldurun.",
  },
  de: {
    title: "Registrierung als Pflegekraft",
    subtitle: "Nach der Registrierung können Sie Ihren Lebenslauf auf Ihrer Profilseite ausfüllen. Sobald Ihr Profil vom Administrator bestätigt wurde, ist es für Kliniken sichtbar.",
    name: "Vorname", surname: "Nachname", email: "E-Mail", password: "Passwort",
    gender: "Geschlecht", genderSelect: "Auswählen…", genderMale: "Männlich", genderFemale: "Weiblich",
    birthDate: "Geburtsdatum", citizenship: "Staatsangehörigkeit", phone: "Telefon", address: "Adresse",
    submit: "Registrieren", submitting: "Wird gesendet…", haveAccount: "Haben Sie ein Konto? Anmelden",
    regFailed: "Registrierung fehlgeschlagen", regSuccess: "Registrierung erfolgreich! Füllen Sie jetzt Ihren Lebenslauf aus.",
  },
  en: {
    title: "Nurse registration",
    subtitle: "After registration you can fill in your CV on your profile page. Once your profile is approved by the admin, clinics will see it.",
    name: "First name", surname: "Last name", email: "Email", password: "Password",
    gender: "Gender", genderSelect: "Select…", genderMale: "Male", genderFemale: "Female",
    birthDate: "Date of birth", citizenship: "Citizenship", phone: "Phone", address: "Address",
    submit: "Register", submitting: "Submitting…", haveAccount: "Already have an account? Log in",
    regFailed: "Registration failed", regSuccess: "Registration successful! Now fill in your CV.",
  },
  ru: {
    title: "Регистрация медработника",
    subtitle: "После регистрации вы сможете заполнить резюме на странице профиля. После подтверждения профиля администратором его увидят клиники.",
    name: "Имя", surname: "Фамилия", email: "Email", password: "Пароль",
    gender: "Пол", genderSelect: "Выберите…", genderMale: "Мужской", genderFemale: "Женский",
    birthDate: "Дата рождения", citizenship: "Гражданство", phone: "Телефон", address: "Адрес",
    submit: "Зарегистрироваться", submitting: "Отправка…", haveAccount: "Уже есть аккаунт? Войти",
    regFailed: "Регистрация не удалась", regSuccess: "Регистрация успешна! Теперь заполните резюме.",
  },
  tr: {
    title: "Hemşire kaydı",
    subtitle: "Kayıttan sonra profil sayfanızda özgeçmişinizi doldurabilirsiniz. Profiliniz yönetici tarafından onaylandıktan sonra klinikler onu görecek.",
    name: "Ad", surname: "Soyad", email: "E-posta", password: "Şifre",
    gender: "Cinsiyet", genderSelect: "Seçin…", genderMale: "Erkek", genderFemale: "Kadın",
    birthDate: "Doğum tarihi", citizenship: "Uyruk", phone: "Telefon", address: "Adres",
    submit: "Kayıt ol", submitting: "Gönderiliyor…", haveAccount: "Hesabınız var mı? Giriş yapın",
    regFailed: "Kayıt başarısız", regSuccess: "Kayıt başarılı! Şimdi özgeçmişinizi doldurun.",
  },
};

export default {
  name: "RegisterNursePage",
  components: { PageCover },
  data() {
    return {
      form: { name: "", surname: "", email: "", password: "", gender: "", birthDate: "", citizenship: "", phone: "", address: "" },
      consentAccepted: false,
      busy: false,
    };
  },
  computed: {
    consentText() {
      const v = this.$store.state.statics?.NurseRegistrationConsent;
      if (v && String(v).trim()) return v;
      const lang = this.$store.state.lang || "az";
      return CONSENT_FALLBACK[lang] || CONSENT_FALLBACK.az;
    },
    // Banner cover-i header-menyuya bağlı səhifədən gəlir (digər səhifələr kimi).
    // Eyni path üçün bir neçə menyu sətri ola bilər: cover-i olan və cari dildə
    // adı dolu olan sətri seçirik.
    cover() {
      const path = this.$route.path;
      const menus = (this.$store.getters["headerMenus/allMenus"] || []).filter((m) => m.path === path);
      const covers = (this.$store.state.pageCover && this.$store.state.pageCover.covers) || [];
      const key = "description_" + (this.$store.state.lang || "az");
      const withCover = menus
        .map((m) => ({ m, c: covers.find((c) => c.typeId === m.aboutTypeId) }))
        .filter((x) => x.c);
      const best = withCover.find((x) => x.m[key] && String(x.m[key]).trim()) || withCover[0];
      return best ? best.c : null;
    },
  },
  methods: {
    tx(key) {
      const sKey = "NurseRegistration" + key.charAt(0).toUpperCase() + key.slice(1);
      const ov = this.$store.state.statics && this.$store.state.statics[sKey];
      if (ov && String(ov).trim()) return ov;
      const lang = this.$store.state.lang || "az";
      return (REG_FALLBACK[lang] || REG_FALLBACK.az)[key] || REG_FALLBACK.az[key] || key;
    },
    async onSubmit() {
      if (!this.consentAccepted) {
        this.$toast.error(this.consentText);
        return;
      }
      this.busy = true;
      try {
        const payload = { ...this.form, consentAccepted: this.consentAccepted };
        if (!payload.birthDate) delete payload.birthDate;
        const role = await this.$store.dispatch("auth/registerNurse", payload);
        if (!role) {
          this.$toast.error(this.tx('regFailed'));
          return;
        }
        this.$toast.success(this.tx('regSuccess'));
        this.$router.push({ name: "profile-nurse" });
      } catch (e) {
        // toast shown by interceptor
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-page { min-height: 60vh; }
.auth-card {
  background: #fff; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 14px rgba(0,0,0,0.06);
  h2 { color: #1e3c72; margin-bottom: 0.25rem; }
  .muted { color: #64748b; margin-bottom: 1.5rem; font-size: 0.9rem; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem;
    @media (max-width: 575px) { grid-template-columns: 1fr; }
  }
  .field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.85rem;
    span { font-size: 0.78rem; color: #475569; font-weight: 600; }
    input, textarea, select { padding: 0.6rem 0.8rem; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit; outline: none; resize: vertical; background: #fff;
      &:focus { border-color: #2a5298; }
    }
  }
  .consent { display: flex; align-items: flex-start; gap: 0.6rem; margin: 0.5rem 0 0.25rem; padding: 0.85rem 1rem; background: #f7faff; border: 1px solid #dbe6f5; border-radius: 8px;
    input[type="checkbox"] { width: 18px; height: 18px; margin-top: 2px; flex-shrink: 0; cursor: pointer; }
    span { font-size: 0.83rem; color: #475569; line-height: 1.5; }
  }
  button.primary { width: 100%; padding: 0.7rem; background: #2a5298; color: #fff; border: 0; border-radius: 6px; cursor: pointer; font: inherit; font-weight: 600; margin-top: 0.5rem;
    &:hover:not(:disabled) { background: #1e3c72; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  }
  .alt { margin-top: 1.25rem; padding-top: 1rem; border-top: 1px solid #eef0f4; text-align: center; font-size: 0.85rem;
    a { color: #2a5298; text-decoration: none; &:hover { text-decoration: underline; } }
  }
}
</style>
